import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BackpackPeopleSection — auto-derived contacts surfaced under the banner.
 *
 * People here come from real shared activity (activities joined together,
 * activities you host with others, or activities under groups you share).
 * No follow / friend / request mechanics. No counts or stats. Tapping a row
 * opens a dialog with shared activities + shared groups + private circle
 * assignment chips. The whole surface is calm and never advertises itself.
 */
export default function BackpackPeopleSection({ token }) {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openContact, setOpenContact] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const reload = useCallback(async () => {
    try {
      const [peopleRes, circlesRes] = await Promise.all([
        axios.get(`${API}/api/people`, { headers }),
        axios.get(`${API}/api/people/circles`, { headers }),
      ]);
      setPeople(peopleRes.data?.people || []);
      setCircles(circlesRes.data?.circles || []);
    } catch {
      setPeople([]); setCircles([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return null;
  if (people.length === 0) return null; // calm: empty state stays invisible

  return (
    <section className="mt-6" data-testid="backpack-people-section">
      <div className="px-4 flex items-center justify-between mb-2">
        <p
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: 'var(--fy-text-disabled)' }}
        >
          People
        </p>
      </div>
      <div className="px-4 flex gap-3 overflow-x-auto no-scrollbar snap-x pb-1" data-testid="backpack-people-list">
        {people.slice(0, 12).map(p => {
          const initials = (p.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('');
          return (
            <button
              key={p.contact_user_id}
              type="button"
              onClick={() => setOpenContact(p)}
              className="relative flex-shrink-0 w-24 snap-start text-left"
              style={{ scrollSnapAlign: 'start' }}
              data-testid={`backpack-people-row-${p.contact_user_id}`}
            >
              <div className="relative w-16 h-16 mx-auto">
                <div
                  className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
                >
                  {p.picture ? (
                    <img src={p.picture} alt="" className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <span className="text-xs font-semibold" style={{ color: 'var(--fy-text-secondary)' }}>
                      {initials.toUpperCase()}
                    </span>
                  )}
                </div>
                {p.circle_ids && p.circle_ids.length > 0 && (
                  <span
                    className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: 'var(--lk-sage)', border: '1.5px solid var(--fy-surface-card)' }}
                    data-testid={`people-circle-marker-${p.contact_user_id}`}
                    aria-label="In a circle"
                  />
                )}
              </div>
              <p className="text-xs font-medium mt-1.5 text-center truncate" style={{ color: 'var(--fy-text-primary)' }}>
                {p.name}
              </p>
              <p className="text-[10px] mt-0.5 text-center truncate" style={{ color: 'var(--fy-text-disabled)' }}>
                {p.context}
              </p>
            </button>
          );
        })}
      </div>

      {openContact && (
        <ContactSheet
          contact={openContact}
          circles={circles}
          token={token}
          onClose={() => setOpenContact(null)}
          onChanged={reload}
          navigate={navigate}
        />
      )}
    </section>
  );
}

function ContactSheet({ contact, circles, token, onClose, onChanged, navigate }) {
  const [shared, setShared] = useState({ activities: [], groups: [] });
  const [assignedIds, setAssignedIds] = useState(contact.circle_ids || []);
  const [newCircleName, setNewCircleName] = useState('');
  const [creating, setCreating] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/people/${contact.contact_user_id}/shared`, { headers })
      .then(res => { if (mounted) setShared({ activities: res.data?.activities || [], groups: res.data?.groups || [] }); })
      .catch(() => { if (mounted) setShared({ activities: [], groups: [] }); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.contact_user_id, token]);

  const toggleCircle = async (circleId) => {
    const isAssigned = assignedIds.includes(circleId);
    const next = isAssigned ? assignedIds.filter(id => id !== circleId) : [...assignedIds, circleId];
    setAssignedIds(next); // optimistic
    try {
      if (isAssigned) {
        await axios.delete(`${API}/api/people/${contact.contact_user_id}/circles/${circleId}`, { headers });
      } else {
        await axios.post(`${API}/api/people/${contact.contact_user_id}/circles/${circleId}`, {}, { headers });
      }
      onChanged && onChanged();
    } catch {
      setAssignedIds(assignedIds); // rollback on failure
    }
  };

  const createAndAssign = async () => {
    const name = newCircleName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/people/circles`, { name }, { headers });
      const circleId = res.data?.circle_id;
      if (circleId) {
        await axios.post(`${API}/api/people/${contact.contact_user_id}/circles/${circleId}`, {}, { headers });
        setNewCircleName('');
        onChanged && onChanged();
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="people-contact-sheet">
        <DialogHeader>
          <DialogTitle>{contact.name}</DialogTitle>
        </DialogHeader>

        {/* Circles — small chips, no labels on hover, no legends */}
        <div data-testid="people-circle-assigner">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
            Circles
          </p>
          <div className="flex flex-wrap gap-2">
            {circles.map(c => {
              const on = assignedIds.includes(c.circle_id);
              return (
                <button
                  key={c.circle_id}
                  type="button"
                  onClick={() => toggleCircle(c.circle_id)}
                  className="px-3 py-1 rounded-full text-xs"
                  style={{
                    backgroundColor: on ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                    color: on ? '#fff' : 'var(--fy-text-secondary)',
                    border: `1px solid ${on ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                  }}
                  data-testid={`people-circle-chip-${c.circle_id}`}
                >
                  {c.name}
                  {on && <X className="w-3 h-3 inline-block ml-1" />}
                </button>
              );
            })}
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={newCircleName}
                onChange={(e) => setNewCircleName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createAndAssign(); }}
                placeholder="New circle"
                maxLength={40}
                className="px-2 py-1 rounded-full text-xs outline-none"
                style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)', width: '110px' }}
                data-testid="people-new-circle-input"
              />
              <button
                type="button"
                onClick={createAndAssign}
                disabled={!newCircleName.trim() || creating}
                className="p-1 rounded-full disabled:opacity-50"
                style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
                data-testid="people-add-circle-btn"
                aria-label="Add circle"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {shared.groups.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
              Shared groups
            </p>
            <div className="space-y-1.5">
              {shared.groups.map(g => (
                <button
                  key={g.group_id}
                  type="button"
                  onClick={() => { onClose(); navigate(`/group/${g.group_id}`); }}
                  className="w-full text-left text-sm hover:underline"
                  style={{ color: 'var(--fy-text-primary)' }}
                  data-testid={`people-shared-group-${g.group_id}`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {shared.activities.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
              Shared activities
            </p>
            <div className="space-y-1.5">
              {shared.activities.slice(0, 8).map(a => (
                <button
                  key={a.activity_id}
                  type="button"
                  onClick={() => { onClose(); navigate(`/activities/${a.activity_id}`); }}
                  className="w-full text-left text-sm hover:underline truncate"
                  style={{ color: 'var(--fy-text-primary)' }}
                  data-testid={`people-shared-activity-${a.activity_id}`}
                >
                  {a.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {shared.groups.length === 0 && shared.activities.length === 0 && (
          <p className="text-sm mt-4" style={{ color: 'var(--fy-text-secondary)' }}>
            No shared activities yet.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
